import type {
	DefaultProps,
	HideableTextShape,
} from "@aerstudios/aerated-types/types";
import { elementIsHideableTextShape } from "@aerstudios/aerated-utilities/dataStructures";
import cx from "clsx";
import React, { useLayoutEffect } from "react";
import * as styles from "./AerFieldset.module.scss";

const normalisedStyles = "default" in styles ? styles.default : styles;

type AerFieldsetProps = DefaultProps<"fieldset"> & {
	/** The label text or hideable text shape */
	label: string | HideableTextShape<string>;
	/** Optional class name for the visible label div */
	labelClassName?: string;
};

/**
 * AerFieldset provides an accessible legend for fieldsets that works correctly in Safari
 * by using a visually hidden legend and a visible div with the same content.
 *
 * This component addresses rendering issues with legend elements in Safari by:
 * 1. Including a properly positioned legend element for all screen readers including touch devices
 * 2. Displaying the same content in a div with proper styling that visually replaces the legend
 *
 * The legend is positioned to overlay the visible text, ensuring touch screen readers can
 * interact with it when users drag their finger over the screen.
 */
export const AerFieldset = React.forwardRef<
	HTMLFieldSetElement,
	AerFieldsetProps
>(({ label, className, labelClassName, children, ...rest }, ref) => {
	const labelText = elementIsHideableTextShape(label) ? label.text : label;
	const shouldHide = elementIsHideableTextShape(label) ? label.hide : false;

	// Create refs for both elements to manage positioning
	const legendRef = React.useRef<HTMLLegendElement>(null);
	const visibleLabelRef = React.useRef<HTMLDivElement>(null);

	// Sync the position of the legend with the visible label
	useLayoutEffect(() => {
		const legend = legendRef.current;
		const visibleLabel = visibleLabelRef.current;

		if (legend && visibleLabel && !shouldHide) {
			// Set the legend's width and position to match the visible label
			const rect = visibleLabel.getBoundingClientRect();
			legend.style.width = `${rect.width}px`;
			legend.style.height = `${rect.height}px`;
		}
	}, [shouldHide]);

	return (
		<fieldset className={className} ref={ref} {...rest}>
			{/* Visually hidden legend for accessibility that overlays the visible text */}
			<legend
				className={cx(normalisedStyles.visuallyHidden, labelClassName)}
				ref={legendRef}
			>
				{labelText}
			</legend>

			{/* Visible div that displays the legend text (unless it should be hidden) */}
			{!shouldHide && (
				<div
					className={cx(normalisedStyles.visibleLabel, labelClassName)}
					aria-hidden="true"
					ref={visibleLabelRef}
				>
					{labelText}
				</div>
			)}
			{children}
		</fieldset>
	);
});

// Add display name for better debugging
AerFieldset.displayName = "AerFieldset";
